import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { Info, Star, Phone, Mail, MapPin, Send, Heart } from 'lucide-react';

const About = () => {
  const { t, i18n } = useTranslation();
  const [review, setReview] = useState({ name: '', rating: 5, message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reviews', review);
      setSubmitted(true);
      setReview({ name: '', rating: 5, message: '' });
    } catch (err) { alert('Failed to submit review'); }
  };

  const contactInfo = [
    { icon: Phone, label: 'Phone', value: '0772425150' },
    { icon: Mail, label: 'Email', value: 'info@interlink.com' },
    { icon: MapPin, label: 'Address', value: 'Kariakoo, Dar es Salaam, Tanzania' },
  ];

  return (
    <div className="space-y-6 md:space-y-8 max-w-4xl mx-auto">
      {/* About Section */}
      <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Info size={24} className="text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">About Interlink</h2>
        </div>
        <div className="space-y-4 text-gray-300 text-sm leading-relaxed">
          <p>
            Interlink is Tanzania's leading business management platform designed to help small and medium businesses 
            grow through better financial management. Our system helps shop owners track stock, manage sales, record expenses, 
            and generate professional financial reports.
          </p>
          <p>
            Founded with a mission to improve business infrastructure across Tanzania and Africa, we provide tools that are 
            simple enough for any business owner to use, yet powerful enough to drive real growth.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/5">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-400">10K+</p>
              <p className="text-gray-500 text-xs mt-1">Active Users</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-400">500K+</p>
              <p className="text-gray-500 text-xs mt-1">Transactions</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-400">2</p>
              <p className="text-gray-500 text-xs mt-1">Languages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Phone size={24} className="text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">Contact Us</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {contactInfo.map((item) => (
            <div key={item.label} className="bg-white/5 rounded-xl p-4 text-center">
              <item.icon size={24} className="text-emerald-400 mx-auto mb-2" />
              <p className="text-gray-400 text-xs">{item.label}</p>
              <p className="text-white text-sm font-medium mt-1">{item.value}</p>
            </div>
          ))}
        </div>
        <div className="text-center text-gray-500 text-xs">
          <p>📱 WhatsApp: 0772425150 | 📧 support@interlink.com</p>
          <p className="mt-1">Working Hours: Monday - Saturday, 8:00 AM - 6:00 PM</p>
        </div>
      </div>

      {/* Review Section */}
      <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Star size={24} className="text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">Leave a Review</h2>
        </div>
        {submitted ? (
          <div className="text-center py-8">
            <Heart size={48} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-medium">Thank you for your review!</p>
            <p className="text-gray-400 text-sm mt-1">We appreciate your feedback.</p>
            <button onClick={() => setSubmitted(false)} className="mt-4 text-emerald-400 text-sm hover:underline">Write another review</button>
          </div>
        ) : (
          <form onSubmit={handleReviewSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Your Name</label>
              <input type="text" value={review.name} onChange={e => setReview({...review, name: e.target.value})}
                placeholder="Enter your name" className="w-full px-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Rating</label>
              <div className="flex gap-2">
                {[1,2,3,4,5].map((star) => (
                  <button key={star} type="button" onClick={() => setReview({...review, rating: star})}
                    className="text-2xl transition">
                    <span className={star <= review.rating ? 'text-yellow-400' : 'text-gray-600'}>★</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Your Review</label>
              <textarea value={review.message} onChange={e => setReview({...review, message: e.target.value})}
                placeholder="Share your experience with Interlink..."
                rows={3} className="w-full px-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50 resize-none" required />
            </div>
            <button type="submit"
              className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition text-sm">
              <Send size={16} /> Submit Review
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default About;
